import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Layout } from "../../components/layout";
import { Search } from "../../components/Search";
import DataTable, { ColumnConfig } from "../../components/DataTable";
import ProvidersFilter from "../../components/ProvidersFilter";
import { selectUser } from "../../store/selectors/authSelectors";
import { PROVIDER_ROLES } from "../../constants";
import { AllPatientsPageStyled } from "./AllPatientsPage.styled";
import { patientsService, Patient } from "../../services/patientsService";
import { Provider } from "../../store/slices/dashboardSlice";
import { colors } from "../../theme/colors";
import { usersService } from "../../services/usersService";

type SortField = "provider" | "patient" | "dob";

// Provider cell component with avatar
const ProviderCell: React.FC<{
  name: string;
  avatarUrl: string | null;
  onClick: () => void;
}> = ({ name, avatarUrl, onClick }) => {
  const getInitials = (str: string) => {
    if (!str || str === "-") return "?";
    return str
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      {avatarUrl ? (
        <Box
          component="img"
          src={avatarUrl}
          alt={name}
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#E8F4F8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: 600,
            color: colors.primary.main,
            flexShrink: 0,
          }}
        >
          {getInitials(name)}
        </Box>
      )}
      <Typography className="clickable">{name}</Typography>
    </Box>
  );
};

interface PatientRow extends Record<string, unknown> {
  id: number;
  externalUid: string;
  providerName: string;
  providerId: number;
  providerAvatarUrl: string | null;
  patientName: string;
  lastName: string;
  dob: string;
  recentPlan: string | null;
  recentPlanId: number | null;
  email: string;
  phone: string;
}

const AllPatientsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector(selectUser);

  // Check if user is Provider
  const isProvider = PROVIDER_ROLES.includes(
    user?.roleTitle as (typeof PROVIDER_ROLES)[number],
  );

  // Get initial provider filter from URL
  const initialProviderIds = useMemo(() => {
    const providerParam = searchParams.get("providerId");
    if (providerParam) {
      return providerParam
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
    }
    return [];
  }, [searchParams]);

  // Data state
  const [patients, setPatients] = useState<Patient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Filters and sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedProviderIds, setSelectedProviderIds] =
    useState<number[]>(initialProviderIds);
  const [sortField, setSortField] = useState<SortField>("patient");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const patientsPerPage = 25;
  const providersLoadedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Format phone number to (XXX) XXX-XXXX
  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    const localDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (localDigits.length === 10) {
      return `(${localDigits.slice(0, 3)}) ${localDigits.slice(
        3,
        6,
      )}-${localDigits.slice(6)}`;
    }
    return phone;
  };

  // Format date to MM/DD/YYYY
  const formatDate = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Debounce search input - 500ms delay to let user finish typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load providers immediately if there are initial provider IDs from URL
  useEffect(() => {
    if (initialProviderIds.length > 0 && !providersLoadedRef.current) {
      setProvidersLoading(true);
      usersService
        .getProviders()
        .then((providersData) => {
          setProviders(providersData);
          providersLoadedRef.current = true;
        })
        .catch((err) => {
          console.error("Error fetching providers:", err);
        })
        .finally(() => {
          setProvidersLoading(false);
        });
    }
  }, [initialProviderIds]);

  // Fetch providers on dropdown open (lazy loading)
  const handleProvidersFilterOpen = useCallback(async () => {
    if (providersLoadedRef.current) return;

    try {
      setProvidersLoading(true);
      const providersData = await usersService.getProviders();
      setProviders(providersData);
      providersLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching providers:", err);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Fetch patients from API
  const fetchPatients = useCallback(async () => {
    // Prevent duplicate requests
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    try {
      // Map sortField to API orderBy
      let orderBy = "Name"; // Sorts by patient name
      if (sortField === "provider") {
        orderBy = "ProviderName";
      } else if (sortField === "dob") {
        orderBy = "DateOfBirth";
      }

      const response = await patientsService.getPatients({
        search: debouncedSearch || undefined,
        providerId:
          selectedProviderIds.length > 0 ? selectedProviderIds : undefined,
        start: (currentPage - 1) * patientsPerPage,
        limit: patientsPerPage,
        total: true,
        orderBy,
        order: sortDirection === "asc" ? 0 : 1,
      });

      setPatients(response.data);
      setTotalCount(response.total);
    } catch (error) {
      console.error("Error fetching patients:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    debouncedSearch,
    selectedProviderIds,
    currentPage,
    patientsPerPage,
    sortField,
    sortDirection,
  ]);

  // Fetch patients when filters change
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Map API data to PatientRow
  const patientRows: PatientRow[] = useMemo(
    () =>
      patients.map((p) => {
        // Construct provider name from firstName and lastName
        const providerName =
          p.providerFirstName || p.providerLastName
            ? `${p.providerFirstName || ""} ${p.providerLastName || ""}`.trim()
            : "-";

        return {
          id: p.id,
          externalUid: p.externalId,
          providerName,
          providerId: p.ownerUserId,
          providerAvatarUrl: p.providerAvatarUrl,
          patientName: `${p.firstName} ${p.lastName}`.trim(),
          lastName: p.lastName,
          dob: p.dateOfBirth,
          recentPlan: p.latestTreatmentPlanName,
          recentPlanId: p.latestTreatmentPlanId,
          email: p.email || "",
          phone: p.phone || "",
        };
      }),
    [patients],
  );

  // Reset page when filters change (except page)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedProviderIds]);

  // Handle provider name click
  const handleProviderClick = (providerId: number) => {
    navigate(`/user-management/${providerId}`);
  };

  // Handle patient name click
  const handlePatientClick = (patientId: number) => {
    navigate(`/patients/${patientId}`);
  };

  // Handle treatment plan click
  const handlePlanClick = (planId: number) => {
    navigate(`/treatment-plan/${planId}`);
  };

  // Columns configuration
  const columns: ColumnConfig<PatientRow>[] = [
    // Provider Name column - only for non-providers
    ...(!isProvider
      ? [
          {
            key: "providerName",
            title: t("allPatients.columns.provider"),
            sortable: true,
            render: (_value: unknown, row: PatientRow) => (
              <ProviderCell
                name={row.providerName}
                avatarUrl={row.providerAvatarUrl}
                onClick={() => handleProviderClick(row.providerId)}
              />
            ),
          } as ColumnConfig<PatientRow>,
        ]
      : []),
    {
      key: "patientName",
      title: t("allPatients.columns.name"),
      sortable: true,
      render: (value: unknown, row: PatientRow) => (
        <Typography
          className="clickable"
          onClick={() => handlePatientClick(row.id)}
        >
          {String(value)}
        </Typography>
      ),
    },
    {
      key: "dob",
      title: t("allPatients.columns.dob"),
      sortable: true,
      render: (value: unknown) => (
        <Typography>{formatDate(String(value))}</Typography>
      ),
    },
    {
      key: "recentPlan",
      title: t("allPatients.columns.plan"),
      render: (value: unknown, row: PatientRow) =>
        value && row.recentPlanId ? (
          <Typography
            className="clickable"
            onClick={() => handlePlanClick(row.recentPlanId!)}
          >
            {String(value)}
          </Typography>
        ) : (
          <Typography>{t("allPatients.noPlanAvailable")}</Typography>
        ),
    },
    {
      key: "email",
      title: t("allPatients.columns.email"),
    },
    {
      key: "phone",
      title: t("allPatients.columns.phone"),
      render: (value: unknown) => (
        <Typography>{formatPhone(String(value))}</Typography>
      ),
    },
  ];

  // Handle sort
  const handleSort = (field: string, direction: "asc" | "desc") => {
    let mappedField: SortField = "patient";
    if (field === "providerName") mappedField = "provider";
    else if (field === "patientName") mappedField = "patient";
    else if (field === "dob") mappedField = "dob";

    setSortField(mappedField);
    setSortDirection(direction);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle provider filter change
  const handleProviderFilterChange = (providerIds: number[]) => {
    setSelectedProviderIds(providerIds);
  };

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Layout>
      <AllPatientsPageStyled>
        <Box className="content-header">
          <Typography variant="h1">{t("allPatients.title")}</Typography>
          <Box className="filters-container">
            <Search
              width={400}
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t("allPatients.searchPlaceholder")}
            />
            {!isProvider && (
              <ProvidersFilter
                providers={providers}
                selectedProviders={selectedProviderIds}
                onProvidersChange={handleProviderFilterChange}
                onOpen={handleProvidersFilterOpen}
                loading={providersLoading}
                width={250}
              />
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable<PatientRow>
            columns={columns}
            data={patientRows}
            sortField={
              sortField === "provider"
                ? "providerName"
                : sortField === "patient"
                  ? "patientName"
                  : "dob"
            }
            sortDirection={sortDirection}
            onSort={handleSort}
            pageSize={patientsPerPage}
            currentPage={currentPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            emptyMessage={t("allPatients.noPatientsFound")}
          />
        )}
      </AllPatientsPageStyled>
    </Layout>
  );
};

export default AllPatientsPage;
